import useSwr, { mutate as globalMutate } from 'swr';
import useSWRMutation, { type MutationFetcher, type SWRMutationConfiguration } from 'swr/mutation';

import type {
    AntennaData,
    AntennaManifest,
    ArchiveSweepFile,
    BaselinesPayload,
    CenterDeviceStatus,
    DeviceMode,
    DeviceStatus,
    WsDeviceInfo,
} from '@ui/Types/Gnss';

import {
    disableNoiseFigureCompensation,
    disableTemperatureCompensation,
    disableVswrCompensation,
    enableNoiseFigureCompensation,
    enableTemperatureCompensation,
    enableVswrCompensation,
    fetchGnssFirmware as fetchGnssFirmwareResponse,
    getGnssAntennaDataJson as getGnssAntennaDataJsonResponse,
    getGnssAntennaManifest as getGnssAntennaManifestResponse,
    getGnssBaseline as getGnssBaselineResponse,
    getGnssDeviceAntennaManifest as getGnssDeviceAntennaManifestResponse,
    getGnssDeviceCenterStatus as getGnssDeviceCenterStatusResponse,
    getGnssDeviceConfig as getGnssDeviceConfigResponse,
    getGnssDevice as getGnssDeviceResponse,
    getGnssDeviceStatus as getGnssDeviceStatusResponse,
    getGnssDeviceStatusSnapshot as getGnssDeviceStatusSnapshotResponse,
    getGnssDevices as getGnssDevicesResponse,
    getGnssIotBindings as getGnssIotBindingsResponse,
    getGnssIotDacState as getGnssIotDacStateResponse,
    getGnssIotDescriptor as getGnssIotDescriptorResponse,
    getGnssIotDevices as getGnssIotDevicesResponse,
    getGnssIotEvents as getGnssIotEventsResponse,
    getGnssLayout as getGnssLayoutResponse,
    getGnssMeasurementReportsList as getGnssMeasurementReportsListResponse,
    gnssBaselineStart,
    gnssBaselineStop,
    gnssClearSweepArchiveFile,
    gnssClearSweepArchives,
    gnssDownloadAllSweepArchives,
    gnssDownloadMeasurementReportBundle,
    gnssDownloadMeasurementReportFile,
    gnssDownloadMeasurementReportSummary,
    gnssDownloadSweepArchiveFile,
    gnssGetAntennaDataBin,
    gnssLiveStart,
    gnssLiveStop,
    gnssLoadBaselineFile,
    gnssPatchDeviceConfig,
    gnssPushFirmware,
    gnssRebootAll,
    gnssRebootDevice,
    gnssRemoveIotBinding,
    gnssRestartAll,
    gnssRestartDevice,
    gnssSaveIotBinding,
    gnssSaveLayout,
    gnssSetIotDacValue,
    gnssSetThresholds,
    gnssStartBaselineBulk,
    gnssStartSave,
    gnssStartSaveBulk,
    gnssStartSweep,
    gnssStartSweepBulk,
    gnssStopBaselineBulk,
    gnssStopSave,
    gnssStopSaveBulk,
    gnssStopSweep,
    gnssStopSweepBulk,
    gnssSubscribeStart,
    gnssSubscribeStop,
    gnssSwitchDeviceMode,
    gnssTurnIotRelayOff,
    gnssTurnIotRelayOn,
    gnssTurnIotRelaysOn,
    gnssUploadMeasurementReport,
    gnssUploadMeasurementReportBundle,
    gnssZeroIotDac,
    listBaselines as listGnssBaselinesResponse,
    listGnssSweepArchives as listGnssSweepArchivesResponse,
    type GnssDeviceConfigPayload,
    type GnssIotBinding,
    type GnssIotBindingsByDevice,
    type GnssIotDacState,
    type GnssIotDescriptor,
    type GnssIotDeviceInfo,
    type GnssIotDeviceMap,
    type GnssIotEvent,
    type MeasurementReportsListResponse,
} from './api';

import type { Key, SWRConfiguration, SWRResponse } from 'swr';

export interface UseSdrMutationHookOptions<Data, ErrorType = Error, Arg = never, MutationKey extends Key = Key> {
    readonly runOnSuccess?: boolean;
    readonly swr?: SWRMutationConfiguration<Data, ErrorType, MutationKey, Arg, Data>;
}

const defaultSdrSwrOptions = {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
} satisfies Pick<SWRConfiguration, 'revalidateOnFocus' | 'revalidateOnReconnect' | 'shouldRetryOnError'>;

async function invalidateSdrKeys(predicate: (key: Key) => boolean) {
    await globalMutate(predicate, undefined, { populateCache: false, revalidate: true });
}

function hasSdrKeyPrefix(key: Key, prefix: string): boolean {
    return Array.isArray(key) && key[0] === prefix;
}

export interface GnssLayoutCoord {
    readonly device_id?: string;
    readonly [key: string]: unknown;
    readonly x_pct?: number;
    readonly y_pct?: number;
}

export interface GnssLayoutData {
    readonly layout?: {
        readonly coords?: readonly GnssLayoutCoord[];
        readonly [key: string]: unknown;
        readonly reference?: string;
    };
    readonly [key: string]: unknown;
}

type GnssDeviceInfoMap = Record<string, WsDeviceInfo> | null;
export type GnssIotDeviceMapData = GnssIotDeviceMap;
export type GnssIotDeviceInfoData = GnssIotDeviceInfo | null;
export type GnssIotDescriptorData = GnssIotDescriptor | null;
export type GnssIotBindingsData = GnssIotBindingsByDevice;
export type GnssIotDacStateData = GnssIotDacState | null;
export type GnssIotEventsData = GnssIotEvent[];
export type { GnssIotBinding, GnssIotDacState, GnssIotDescriptor, GnssIotDeviceInfo, GnssIotEvent };
type GnssFirmwareData = null | Record<string, unknown>;
export interface GnssDeviceStatusSnapshotData {
    actMode: DeviceMode | null;
    centerStatus: CenterDeviceStatus | null;
    cfgMode: DeviceMode | null;
    sweepStatus: DeviceStatus | null;
}
type GnssSwrQueryResult<Data, ErrorType, QueryKey extends Key> = SWRResponse<Data, ErrorType> & {
    readonly swrKey: null | QueryKey;
};
type GnssQuerySwrOptions<Data, ErrorType = Error> = SWRConfiguration<Data, ErrorType> & {
    readonly enabled?: boolean;
    readonly swrKey?: Key;
};

export type GnssQueryHookOptions<Data, ErrorType = Error> = Readonly<{
    readonly request?: RequestInit;
    readonly swr?: GnssQuerySwrOptions<Data, ErrorType>;
}>;

interface GnssDeviceInvalidationOptions {
    readonly includeDevicesList?: boolean;
}

interface GnssDeviceSerialArg {
    readonly deviceSerial: string;
}

interface GnssDeviceSerialFilenameArg extends GnssDeviceSerialArg {
    readonly filename: string;
}

interface GnssDeviceSerialSessionArg extends GnssDeviceSerialArg {
    readonly session?: string;
}

interface GnssDeviceSerialSweepsArg extends GnssDeviceSerialArg {
    readonly sweeps?: number;
}

interface GnssDeviceSerialsArg {
    readonly deviceSerials: readonly string[];
}

interface GnssDeviceSerialsSweepsArg extends GnssDeviceSerialsArg {
    readonly sweeps: number;
}

interface GnssDeviceConfigUpdateArg extends GnssDeviceSerialArg {
    readonly update: Record<string, unknown>;
}

interface GnssCompensationToggleArg extends GnssDeviceSerialArg {
    readonly enabled: boolean;
}

type GnssCompensationActionResult =
    | { readonly data: unknown; readonly ok: boolean }
    | { readonly error: 'network'; readonly ok: false };

type GnssCompensationAction = (deviceSerial: string) => Promise<GnssCompensationActionResult>;

const runEnableVswrCompensation = enableVswrCompensation as unknown as GnssCompensationAction;
const runDisableVswrCompensation = disableVswrCompensation as unknown as GnssCompensationAction;
const runEnableNoiseFigureCompensation = enableNoiseFigureCompensation as unknown as GnssCompensationAction;
const runDisableNoiseFigureCompensation = disableNoiseFigureCompensation as unknown as GnssCompensationAction;
const runEnableTemperatureCompensation = enableTemperatureCompensation as unknown as GnssCompensationAction;
const runDisableTemperatureCompensation = disableTemperatureCompensation as unknown as GnssCompensationAction;

interface GnssThresholdsArg extends GnssDeviceSerialArg {
    readonly params: Parameters<typeof gnssSetThresholds>[1];
}

interface GnssLayoutSaveArg {
    readonly coords: Parameters<typeof gnssSaveLayout>[1];
    readonly reference: string;
}

interface GnssPushFirmwareArg {
    readonly scope: string;
    readonly version: string | null;
}

interface GnssSwitchDeviceModeArg extends GnssDeviceSerialArg {
    readonly mode: DeviceMode;
}

interface GnssDownloadAntennaBinArg {
    readonly antennaId: string;
}

interface GnssIotIdArg {
    readonly iotId: string;
}

interface GnssIotPinRefArg extends GnssIotIdArg {
    readonly pinRef: string;
}

interface GnssIotSetDacArg extends GnssIotPinRefArg {
    readonly value: number;
}

interface GnssIotRelayOnArg extends GnssIotPinRefArg {
    readonly timeoutSeconds: number;
    readonly username: string;
}

interface GnssIotRelaysOnArg extends GnssIotIdArg {
    readonly pinRefs: readonly string[];
    readonly timeoutSeconds?: null | number;
    readonly username: string;
    readonly value: number;
}

interface GnssIotBindingArg extends GnssIotIdArg {
    readonly alt?: null | number;
    readonly boundDeviceId: string;
    readonly kind?: null | string;
    readonly label?: null | string;
    readonly lat: number;
    readonly lon: number;
    readonly pinId: string;
}

interface GnssIotRelayOffArg extends GnssIotPinRefArg {
    readonly username: string;
}

interface GnssIotBindingDeleteArg extends GnssIotIdArg {
    readonly pinId: string;
}

function getGnssQueryOptions<Data, ErrorType = Error>(
    options?: GnssQuerySwrOptions<Data, ErrorType>,
): SWRConfiguration<Data, ErrorType> {
    const { enabled: _enabled, swrKey: _swrKey, ...swrOptions } = options ?? {};

    return {
        ...defaultSdrSwrOptions,
        ...swrOptions,
    };
}

function buildGnssSwrQueryResult<Data, ErrorType, QueryKey extends Key>(
    swrKey: null | QueryKey,
    query: SWRResponse<Data, ErrorType>,
): GnssSwrQueryResult<Data, ErrorType, QueryKey> {
    return {
        swrKey,
        ...query,
    };
}

function createGnssRequestError(
    action: string,
    response: { readonly error: 'network'; readonly ok: false } | { readonly ok: boolean },
) {
    if ('error' in response) {
        return new Error(`Network error while ${action}`);
    }

    return new Error(`Failed to ${action}`);
}

function createGnssActionError(
    action: string,
    response: { readonly error?: string; readonly ok: false } | { readonly ok: boolean },
) {
    if ('error' in response && response.error === 'network') {
        return new Error(`Network error while ${action}`);
    }

    return new Error(`Failed to ${action}`);
}

async function loadGnssData<Data>(
    load: () => Promise<{ data: Data; ok: boolean } | { error: 'network'; ok: false }>,
    action: string,
): Promise<Data> {
    const response = await load();

    if (!response.ok) {
        throw createGnssRequestError(action, response);
    }

    return response.data;
}

async function loadNullableGnssData<Data>(
    load: () => Promise<{ data: Data | null; ok: boolean } | { error: 'network'; ok: false }>,
    action: string,
): Promise<Data | null> {
    const response = await load();

    if (!response.ok) {
        throw createGnssRequestError(action, response);
    }

    return response.data ?? null;
}

async function loadGnssArrayData<Item>(
    load: () => Promise<{ data: Item[] | null; ok: boolean } | { error: 'network'; ok: false }>,
    action: string,
): Promise<Item[]> {
    const response = await load();

    if (!response.ok) {
        throw createGnssRequestError(action, response);
    }

    return response.data ?? [];
}

async function loadGnssLayoutData(
    load: () => Promise<{ data: unknown; ok: boolean } | { error: 'network'; ok: false }>,
    action: string,
): Promise<GnssLayoutData | null> {
    const response = await load();

    if (!response.ok) {
        throw createGnssRequestError(action, response);
    }

    if (!response.data || typeof response.data !== 'object') {
        return null;
    }

    return response.data as GnssLayoutData;
}

function matchesGnssDeviceKey(key: Key, prefix: string, deviceSerial: string) {
    return Array.isArray(key) && key[0] === prefix && key[1] === deviceSerial;
}

function matchesGnssPrefix(key: Key, prefix: string) {
    return hasSdrKeyPrefix(key, prefix);
}

async function invalidateGnssDeviceScopedKeys(
    deviceSerial: string,
    devicePrefixes: readonly string[],
    globalPrefixes: readonly string[] = [],
) {
    await invalidateSdrKeys((key) => {
        return (
            devicePrefixes.some((prefix) => matchesGnssDeviceKey(key, prefix, deviceSerial)) ||
            globalPrefixes.some((prefix) => matchesGnssPrefix(key, prefix))
        );
    });
}

async function invalidateGnssGlobalPrefixes(prefixes: readonly string[]) {
    await invalidateSdrKeys((key) => prefixes.some((prefix) => matchesGnssPrefix(key, prefix)));
}

async function invalidateGnssDeviceMetadata(deviceSerial: string, options?: GnssDeviceInvalidationOptions) {
    await invalidateGnssDeviceScopedKeys(
        deviceSerial,
        [getGetGnssDeviceKey('')[0]],
        options?.includeDevicesList ? [getGetGnssDevicesKey()[0]] : [],
    );
}

async function invalidateGnssDeviceStatus(deviceSerial: string, options?: GnssDeviceInvalidationOptions) {
    await invalidateGnssDeviceScopedKeys(
        deviceSerial,
        [getGetGnssDeviceStatusKey('')[0], getGetGnssDeviceCenterStatusKey('')[0]],
        options?.includeDevicesList ? [getGetGnssDevicesKey()[0]] : [],
    );
}

async function invalidateGnssDeviceConfig(deviceSerial: string) {
    await invalidateGnssDeviceScopedKeys(deviceSerial, [getGetGnssDeviceConfigKey('')[0]]);
}

async function invalidateGnssDeviceBaseline(deviceSerial: string) {
    await invalidateGnssDeviceScopedKeys(deviceSerial, [getGetGnssBaselineKey('')[0], getListGnssBaselinesKey('')[0]]);
}

async function invalidateGnssDeviceArchives(deviceSerial: string) {
    await invalidateGnssDeviceScopedKeys(deviceSerial, [getListGnssSweepArchivesKey('')[0]]);
}

async function invalidateGnssConfigReadModels(deviceSerial: string) {
    await invalidateGnssDeviceConfig(deviceSerial);
}

async function invalidateGnssCompensationReadModels(deviceSerial: string) {
    await Promise.all([
        invalidateGnssDeviceConfig(deviceSerial),
        invalidateGnssDeviceMetadata(deviceSerial, { includeDevicesList: true }),
        invalidateGnssDeviceStatus(deviceSerial, { includeDevicesList: true }),
        invalidateGnssDeviceScopedKeys(deviceSerial, [getGetGnssDeviceAntennaManifestKey('')[0]]),
    ]);
}

async function invalidateGnssBaselineReadModels(deviceSerial: string) {
    await Promise.all([invalidateGnssDeviceBaseline(deviceSerial), invalidateGnssDeviceStatus(deviceSerial)]);
}

async function invalidateGnssStatusReadModels(deviceSerial: string) {
    await invalidateGnssDeviceStatus(deviceSerial);
}

async function invalidateGnssPowerReadModels(deviceSerial: string) {
    await Promise.all([
        invalidateGnssDeviceMetadata(deviceSerial, { includeDevicesList: true }),
        invalidateGnssDeviceStatus(deviceSerial, { includeDevicesList: true }),
    ]);
}

async function invalidateGnssDeviceModeState(deviceSerial: string) {
    await Promise.all([
        invalidateGnssDeviceConfig(deviceSerial),
        invalidateGnssDeviceStatus(deviceSerial),
        invalidateGnssDeviceScopedKeys(deviceSerial, [getGetGnssDeviceStatusSnapshotKey('')[0]]),
    ]);
}

async function invalidateGnssLayoutData() {
    await invalidateGnssGlobalPrefixes([getGetGnssLayoutKey()[0]]);
}

async function invalidateGnssFirmwareData() {
    await invalidateGnssGlobalPrefixes([getGetGnssFirmwareKey()[0], getGetGnssDevicesKey()[0]]);
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
async function invalidateGnssIotDevices() {
    await invalidateGnssGlobalPrefixes([getGetGnssIotDevicesKey()[0]]);
}

async function invalidateGnssIotBindings() {
    await invalidateGnssGlobalPrefixes([getGetGnssIotBindingsKey()[0]]);
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
async function invalidateGnssIotDescriptor(iotId: string) {
    await invalidateSdrKeys((key) => matchesGnssDeviceKey(key, getGetGnssIotDescriptorKey('')[0], iotId));
}

async function invalidateGnssIotDacState(iotId: string, pinRef: string) {
    await invalidateSdrKeys(
        (key) =>
            Array.isArray(key) &&
            key[0] === getGetGnssIotDacStateKey('', '')[0] &&
            key[1] === iotId &&
            key[2] === pinRef,
    );
}

async function invalidateGnssIotEvents(iotId: string) {
    await invalidateSdrKeys(
        (key) => Array.isArray(key) && key[0] === getGetGnssIotEventsKey('', 50)[0] && key[1] === iotId,
    );
}

async function invalidateGnssIotRuntime(iotId: string, pinRef?: string) {
    await Promise.all([
        invalidateGnssIotEvents(iotId),
        pinRef ? invalidateGnssIotDacState(iotId, pinRef) : Promise.resolve(),
    ]);
}

async function invalidateGnssIotBindingsAndEvents(iotId: string) {
    await Promise.all([invalidateGnssIotBindings(), invalidateGnssIotEvents(iotId)]);
}

function runGnssMutationSuccessEffect<Response extends { ok: boolean }, Arg>(
    action: string,
    onSuccess: ((response: Response, arg: Arg) => Promise<void> | void) | undefined,
    response: Response,
    arg: Arg,
) {
    if (!onSuccess) {
        return;
    }

    void Promise.resolve()
        .then(() => onSuccess(response, arg))
        .catch((error: unknown) => {
            console.error(`Failed post-success work for ${action}`, error);
        });
}

function useGnssMutationAction<Response extends { ok: boolean }, Arg, MutationKey extends readonly unknown[]>(params: {
    readonly action: string;
    readonly execute: (arg: Arg) => Promise<Response>;
    readonly onSuccess?: (response: Response, arg: Arg) => Promise<void> | void;
    readonly options?: Readonly<UseSdrMutationHookOptions<Response, Error, Arg, MutationKey>>;
    readonly swrKey: MutationKey;
}) {
    type MutationOptions = SWRMutationConfiguration<Response, Error, MutationKey, Arg, Response> & {
        throwOnError?: boolean;
    };

    const optionContainer = params.options as
        | { readonly runOnSuccess?: boolean; readonly swr?: MutationOptions }
        | undefined;
    const runOnSuccess = optionContainer?.runOnSuccess ?? true;
    const swrOptions = optionContainer?.swr;
    const fetcher = (async (_key: MutationKey, { arg }: Readonly<{ arg: Arg }>) => {
        const response = await params.execute(arg);

        if (!response.ok) {
            throw createGnssActionError(params.action, response);
        }

        if (runOnSuccess) {
            runGnssMutationSuccessEffect(params.action, params.onSuccess, response, arg);
        }

        return response;
    }) as MutationFetcher<Response, MutationKey, Arg>;
    const query = useSWRMutation<Response, Error, MutationKey, Arg>(params.swrKey, fetcher, swrOptions);

    return {
        swrKey: params.swrKey,
        ...query,
    };
}

export const getGetGnssDevicesUrl = () => '/devices';
export const getGetGnssDevicesKey = () => ['gnss-devices'] as const;

export async function getGnssDevices(_request?: RequestInit): Promise<GnssDeviceInfoMap> {
    void _request;

    return loadNullableGnssData(() => getGnssDevicesResponse(), 'fetch GNSS devices');
}

export type GetGnssDevicesQueryResult = GnssDeviceInfoMap;
export type GetGnssDevicesQueryError = Error;

export function useGetGnssDevices(options?: GnssQueryHookOptions<GetGnssDevicesQueryResult>) {
    const enabled = options?.swr?.enabled !== false;
    const swrKey = enabled ? (options?.swr?.swrKey ?? getGetGnssDevicesKey()) : null;
    const query = useSwr<GetGnssDevicesQueryResult, GetGnssDevicesQueryError>(
        swrKey,
        () => getGnssDevices(options?.request),
        getGnssQueryOptions(options?.swr),
    );

    return buildGnssSwrQueryResult(swrKey, query);
}

export const getGetGnssMeasurementReportsListKey = () => ['gnss-measurement-reports-list'] as const;

export async function getGnssMeasurementReportsList(
    _request?: RequestInit,
): Promise<MeasurementReportsListResponse | null> {
    void _request;

    return loadNullableGnssData(() => getGnssMeasurementReportsListResponse(), 'fetch GNSS measurement reports list');
}

export type GetGnssMeasurementReportsListQueryResult = MeasurementReportsListResponse | null;
export type GetGnssMeasurementReportsListQueryError = Error;

export function useGetGnssMeasurementReportsList(
    options?: GnssQueryHookOptions<GetGnssMeasurementReportsListQueryResult>,
) {
    const enabled = options?.swr?.enabled !== false;
    const swrKey = enabled ? (options?.swr?.swrKey ?? getGetGnssMeasurementReportsListKey()) : null;
    const query = useSwr<GetGnssMeasurementReportsListQueryResult, GetGnssMeasurementReportsListQueryError>(
        swrKey,
        () => getGnssMeasurementReportsList(options?.request),
        getGnssQueryOptions(options?.swr),
    );

    return buildGnssSwrQueryResult(swrKey, query);
}

export const getGetGnssDeviceUrl = (deviceSerial: string) => `/devices/${encodeURIComponent(deviceSerial)}`;
export const getGetGnssDeviceKey = (deviceSerial: string) => ['gnss-device', deviceSerial] as const;

export async function getGnssDevice(deviceSerial: string, _request?: RequestInit): Promise<WsDeviceInfo | null> {
    void _request;

    return loadNullableGnssData(() => getGnssDeviceResponse(deviceSerial), 'fetch GNSS device');
}

export type GetGnssDeviceQueryResult = WsDeviceInfo | null;
export type GetGnssDeviceQueryError = Error;

export function useGetGnssDevice(
    deviceSerial: null | string | undefined,
    options?: GnssQueryHookOptions<GetGnssDeviceQueryResult>,
) {
    const enabled = Boolean(deviceSerial) && options?.swr?.enabled !== false;
    const swrKey = enabled ? (options?.swr?.swrKey ?? getGetGnssDeviceKey(deviceSerial ?? '')) : null;
    const query = useSwr<GetGnssDeviceQueryResult, GetGnssDeviceQueryError>(
        swrKey,
        () => getGnssDevice(deviceSerial ?? '', options?.request),
        getGnssQueryOptions(options?.swr),
    );

    return buildGnssSwrQueryResult(swrKey, query);
}

export const getGetGnssDeviceConfigUrl = (deviceSerial: string) =>
    `/devices/${encodeURIComponent(deviceSerial)}/config`;
export const getGetGnssDeviceConfigKey = (deviceSerial: string) => ['gnss-device-config', deviceSerial] as const;

export async function getGnssDeviceConfig(
    deviceSerial: string,
    _request?: RequestInit,
): Promise<GnssDeviceConfigPayload> {
    void _request;

    return loadGnssData(() => getGnssDeviceConfigResponse(deviceSerial), 'fetch GNSS device config');
}

export type GetGnssDeviceConfigQueryResult = GnssDeviceConfigPayload;
export type GetGnssDeviceConfigQueryError = Error;
export type { GnssDeviceConfigPayload };

export function useGetGnssDeviceConfig(
    deviceSerial: null | string | undefined,
    options?: GnssQueryHookOptions<GetGnssDeviceConfigQueryResult>,
) {
    const enabled = Boolean(deviceSerial) && options?.swr?.enabled !== false;
    const swrKey = enabled ? (options?.swr?.swrKey ?? getGetGnssDeviceConfigKey(deviceSerial ?? '')) : null;
    const query = useSwr<GetGnssDeviceConfigQueryResult, GetGnssDeviceConfigQueryError>(
        swrKey,
        () => getGnssDeviceConfig(deviceSerial ?? '', options?.request),
        getGnssQueryOptions(options?.swr),
    );

    return buildGnssSwrQueryResult(swrKey, query);
}

export const getGetGnssDeviceStatusUrl = (deviceSerial: string) =>
    `/devices/${encodeURIComponent(deviceSerial)}/status`;
export const getGetGnssDeviceStatusKey = (deviceSerial: string) => ['gnss-device-status', deviceSerial] as const;

export async function getGnssDeviceStatus(deviceSerial: string, _request?: RequestInit): Promise<DeviceStatus | null> {
    void _request;

    return loadNullableGnssData(() => getGnssDeviceStatusResponse(deviceSerial), 'fetch GNSS device status');
}

export type GetGnssDeviceStatusQueryResult = DeviceStatus | null;
export type GetGnssDeviceStatusQueryError = Error;

export function useGetGnssDeviceStatus(
    deviceSerial: null | string | undefined,
    options?: GnssQueryHookOptions<GetGnssDeviceStatusQueryResult>,
) {
    const enabled = Boolean(deviceSerial) && options?.swr?.enabled !== false;
    const swrKey = enabled ? (options?.swr?.swrKey ?? getGetGnssDeviceStatusKey(deviceSerial ?? '')) : null;
    const query = useSwr<GetGnssDeviceStatusQueryResult, GetGnssDeviceStatusQueryError>(
        swrKey,
        () => getGnssDeviceStatus(deviceSerial ?? '', options?.request),
        getGnssQueryOptions(options?.swr),
    );

    return buildGnssSwrQueryResult(swrKey, query);
}

export const getGetGnssDeviceStatusSnapshotUrl = (deviceSerial: string) =>
    `/devices/${encodeURIComponent(deviceSerial)}/status`;
export const getGetGnssDeviceStatusSnapshotKey = (deviceSerial: string) =>
    ['gnss-device-status-snapshot', deviceSerial] as const;

export async function getGnssDeviceStatusSnapshot(
    deviceSerial: string,
    _request?: RequestInit,
): Promise<GnssDeviceStatusSnapshotData> {
    void _request;

    return loadGnssData(() => getGnssDeviceStatusSnapshotResponse(deviceSerial), 'fetch GNSS device status snapshot');
}

export type GetGnssDeviceStatusSnapshotQueryResult = GnssDeviceStatusSnapshotData;
export type GetGnssDeviceStatusSnapshotQueryError = Error;

export function useGetGnssDeviceStatusSnapshot(
    deviceSerial: null | string | undefined,
    options?: GnssQueryHookOptions<GetGnssDeviceStatusSnapshotQueryResult>,
) {
    const enabled = Boolean(deviceSerial) && options?.swr?.enabled !== false;
    const swrKey = enabled ? (options?.swr?.swrKey ?? getGetGnssDeviceStatusSnapshotKey(deviceSerial ?? '')) : null;
    const query = useSwr<GetGnssDeviceStatusSnapshotQueryResult, GetGnssDeviceStatusSnapshotQueryError>(
        swrKey,
        () => getGnssDeviceStatusSnapshot(deviceSerial ?? '', options?.request),
        getGnssQueryOptions(options?.swr),
    );

    return buildGnssSwrQueryResult(swrKey, query);
}

export const getGetGnssDeviceCenterStatusUrl = (deviceSerial: string) =>
    `/devices/${encodeURIComponent(deviceSerial)}/status`;
export const getGetGnssDeviceCenterStatusKey = (deviceSerial: string) =>
    ['gnss-device-center-status', deviceSerial] as const;

export async function getGnssDeviceCenterStatus(
    deviceSerial: string,
    _request?: RequestInit,
): Promise<CenterDeviceStatus | null> {
    void _request;

    return loadNullableGnssData(() => getGnssDeviceCenterStatusResponse(deviceSerial), 'fetch GNSS center status');
}

export type GetGnssDeviceCenterStatusQueryResult = CenterDeviceStatus | null;
export type GetGnssDeviceCenterStatusQueryError = Error;

export function useGetGnssDeviceCenterStatus(
    deviceSerial: null | string | undefined,
    options?: GnssQueryHookOptions<GetGnssDeviceCenterStatusQueryResult>,
) {
    const enabled = Boolean(deviceSerial) && options?.swr?.enabled !== false;
    const swrKey = enabled ? (options?.swr?.swrKey ?? getGetGnssDeviceCenterStatusKey(deviceSerial ?? '')) : null;
    const query = useSwr<GetGnssDeviceCenterStatusQueryResult, GetGnssDeviceCenterStatusQueryError>(
        swrKey,
        () => getGnssDeviceCenterStatus(deviceSerial ?? '', options?.request),
        getGnssQueryOptions(options?.swr),
    );

    return buildGnssSwrQueryResult(swrKey, query);
}

export const getGetGnssBaselineUrl = (deviceSerial: string) => `/devices/${encodeURIComponent(deviceSerial)}/baseline`;
export const getGetGnssBaselineKey = (deviceSerial: string) => ['gnss-device-baseline', deviceSerial] as const;

export async function getGnssBaseline(deviceSerial: string, _request?: RequestInit): Promise<BaselinesPayload | null> {
    void _request;

    const response = await getGnssBaselineResponse(deviceSerial);

    if (!response.ok) {
        return null;
    }

    return response.data ?? null;
}

export type GetGnssBaselineQueryResult = BaselinesPayload | null;
export type GetGnssBaselineQueryError = Error;

export function useGetGnssBaseline(
    deviceSerial: null | string | undefined,
    options?: GnssQueryHookOptions<GetGnssBaselineQueryResult>,
) {
    const enabled = Boolean(deviceSerial) && options?.swr?.enabled !== false;
    const swrKey = enabled ? (options?.swr?.swrKey ?? getGetGnssBaselineKey(deviceSerial ?? '')) : null;
    const query = useSwr<GetGnssBaselineQueryResult, GetGnssBaselineQueryError>(
        swrKey,
        () => getGnssBaseline(deviceSerial ?? '', options?.request),
        getGnssQueryOptions(options?.swr),
    );

    return buildGnssSwrQueryResult(swrKey, query);
}

export const getListGnssBaselinesUrl = (deviceSerial: string) =>
    `/devices/${encodeURIComponent(deviceSerial)}/baseline/list`;
export const getListGnssBaselinesKey = (deviceSerial: string) => ['gnss-device-baseline-list', deviceSerial] as const;

export async function listGnssBaselines(deviceSerial: string, _request?: RequestInit): Promise<BaselinesPayload[]> {
    void _request;

    return loadGnssArrayData(() => listGnssBaselinesResponse(deviceSerial), 'fetch GNSS baseline list');
}

export type ListGnssBaselinesQueryResult = BaselinesPayload[];
export type ListGnssBaselinesQueryError = Error;

export function useListGnssBaselines(
    deviceSerial: null | string | undefined,
    options?: GnssQueryHookOptions<ListGnssBaselinesQueryResult>,
) {
    const enabled = Boolean(deviceSerial) && options?.swr?.enabled !== false;
    const swrKey = enabled ? (options?.swr?.swrKey ?? getListGnssBaselinesKey(deviceSerial ?? '')) : null;
    const query = useSwr<ListGnssBaselinesQueryResult, ListGnssBaselinesQueryError>(
        swrKey,
        () => listGnssBaselines(deviceSerial ?? '', options?.request),
        getGnssQueryOptions(options?.swr),
    );

    return buildGnssSwrQueryResult(swrKey, query);
}

export const getListGnssSweepArchivesUrl = (deviceSerial: string) =>
    `/devices/${encodeURIComponent(deviceSerial)}/archive/sweeps/list`;
export const getListGnssSweepArchivesKey = (deviceSerial: string) => ['gnss-sweep-archives', deviceSerial] as const;

export async function listGnssSweepArchives(deviceSerial: string, _request?: RequestInit): Promise<ArchiveSweepFile[]> {
    void _request;

    return loadGnssArrayData(() => listGnssSweepArchivesResponse(deviceSerial), 'fetch GNSS sweep archives');
}

export type ListGnssSweepArchivesQueryResult = ArchiveSweepFile[];
export type ListGnssSweepArchivesQueryError = Error;

export function useListGnssSweepArchives(
    deviceSerial: null | string | undefined,
    options?: GnssQueryHookOptions<ListGnssSweepArchivesQueryResult>,
) {
    const enabled = Boolean(deviceSerial) && options?.swr?.enabled !== false;
    const swrKey = enabled ? (options?.swr?.swrKey ?? getListGnssSweepArchivesKey(deviceSerial ?? '')) : null;
    const query = useSwr<ListGnssSweepArchivesQueryResult, ListGnssSweepArchivesQueryError>(
        swrKey,
        () => listGnssSweepArchives(deviceSerial ?? '', options?.request),
        getGnssQueryOptions(options?.swr),
    );

    return buildGnssSwrQueryResult(swrKey, query);
}

export const getGetGnssLayoutUrl = () => '/layout?reference';
export const getGetGnssLayoutKey = () => ['gnss-layout'] as const;

export async function getGnssLayout(_request?: RequestInit): Promise<GnssLayoutData | null> {
    void _request;

    return loadGnssLayoutData(() => getGnssLayoutResponse(), 'fetch GNSS layout');
}

export type GetGnssLayoutQueryResult = GnssLayoutData | null;
export type GetGnssLayoutQueryError = Error;

export function useGetGnssLayout(options?: GnssQueryHookOptions<GetGnssLayoutQueryResult>) {
    const enabled = options?.swr?.enabled !== false;
    const swrKey = enabled ? (options?.swr?.swrKey ?? getGetGnssLayoutKey()) : null;
    const query = useSwr<GetGnssLayoutQueryResult, GetGnssLayoutQueryError>(
        swrKey,
        () => getGnssLayout(options?.request),
        getGnssQueryOptions(options?.swr),
    );

    return buildGnssSwrQueryResult(swrKey, query);
}

export const getGetGnssIotDevicesUrl = () => '/iot';
export const getGetGnssIotDevicesKey = () => ['gnss-iot-devices'] as const;

export async function getGnssIotDevices(_request?: RequestInit): Promise<GnssIotDeviceMapData> {
    void _request;

    return loadGnssData(() => getGnssIotDevicesResponse(), 'fetch GNSS IoT devices');
}

export type GetGnssIotDevicesQueryResult = GnssIotDeviceMapData;
export type GetGnssIotDevicesQueryError = Error;

export function useGetGnssIotDevices(options?: GnssQueryHookOptions<GetGnssIotDevicesQueryResult>) {
    const enabled = options?.swr?.enabled !== false;
    const swrKey = enabled ? (options?.swr?.swrKey ?? getGetGnssIotDevicesKey()) : null;
    const query = useSwr<GetGnssIotDevicesQueryResult, GetGnssIotDevicesQueryError>(
        swrKey,
        () => getGnssIotDevices(options?.request),
        getGnssQueryOptions(options?.swr),
    );

    return buildGnssSwrQueryResult(swrKey, query);
}

export const getGetGnssIotDescriptorUrl = (iotId: string) => `/iot/${encodeURIComponent(iotId)}/descriptor`;
export const getGetGnssIotDescriptorKey = (iotId: string) => ['gnss-iot-descriptor', iotId] as const;

export async function getGnssIotDescriptor(iotId: string, _request?: RequestInit): Promise<GnssIotDescriptorData> {
    void _request;

    return loadNullableGnssData(() => getGnssIotDescriptorResponse(iotId), 'fetch GNSS IoT descriptor');
}

export type GetGnssIotDescriptorQueryResult = GnssIotDescriptorData;
export type GetGnssIotDescriptorQueryError = Error;

export function useGetGnssIotDescriptor(
    iotId: null | string | undefined,
    options?: GnssQueryHookOptions<GetGnssIotDescriptorQueryResult>,
) {
    const enabled = Boolean(iotId) && options?.swr?.enabled !== false;
    const swrKey = enabled ? (options?.swr?.swrKey ?? getGetGnssIotDescriptorKey(iotId ?? '')) : null;
    const query = useSwr<GetGnssIotDescriptorQueryResult, GetGnssIotDescriptorQueryError>(
        swrKey,
        () => getGnssIotDescriptor(iotId ?? '', options?.request),
        getGnssQueryOptions(options?.swr),
    );

    return buildGnssSwrQueryResult(swrKey, query);
}

export const getGetGnssIotEventsUrl = (iotId: string, limit = 50) =>
    `/iot/${encodeURIComponent(iotId)}/events?limit=${encodeURIComponent(limit.toString())}`;
export const getGetGnssIotEventsKey = (iotId: string, limit = 50) => ['gnss-iot-events', iotId, limit] as const;

export async function getGnssIotEvents(iotId: string, limit = 50, _request?: RequestInit): Promise<GnssIotEventsData> {
    void _request;

    return loadGnssData(() => getGnssIotEventsResponse(iotId, limit), 'fetch GNSS IoT events');
}

export type GetGnssIotEventsQueryResult = GnssIotEventsData;
export type GetGnssIotEventsQueryError = Error;

export function useGetGnssIotEvents(
    iotId: null | string | undefined,
    limit = 50,
    options?: GnssQueryHookOptions<GetGnssIotEventsQueryResult>,
) {
    const enabled = Boolean(iotId) && options?.swr?.enabled !== false;
    const swrKey = enabled ? (options?.swr?.swrKey ?? getGetGnssIotEventsKey(iotId ?? '', limit)) : null;
    const query = useSwr<GetGnssIotEventsQueryResult, GetGnssIotEventsQueryError>(
        swrKey,
        () => getGnssIotEvents(iotId ?? '', limit, options?.request),
        getGnssQueryOptions(options?.swr),
    );

    return buildGnssSwrQueryResult(swrKey, query);
}

export const getGetGnssIotBindingsUrl = () => '/iot/bindings';
export const getGetGnssIotBindingsKey = () => ['gnss-iot-bindings'] as const;

export async function getGnssIotBindings(_request?: RequestInit): Promise<GnssIotBindingsData> {
    void _request;

    return loadGnssData(() => getGnssIotBindingsResponse(), 'fetch GNSS IoT bindings');
}

export type GetGnssIotBindingsQueryResult = GnssIotBindingsData;
export type GetGnssIotBindingsQueryError = Error;

export function useGetGnssIotBindings(options?: GnssQueryHookOptions<GetGnssIotBindingsQueryResult>) {
    const enabled = options?.swr?.enabled !== false;
    const swrKey = enabled ? (options?.swr?.swrKey ?? getGetGnssIotBindingsKey()) : null;
    const query = useSwr<GetGnssIotBindingsQueryResult, GetGnssIotBindingsQueryError>(
        swrKey,
        () => getGnssIotBindings(options?.request),
        getGnssQueryOptions(options?.swr),
    );

    return buildGnssSwrQueryResult(swrKey, query);
}

export const getGetGnssIotDacStateUrl = (iotId: string, pinRef: string) =>
    `/iot/${encodeURIComponent(iotId)}/dac/${encodeURIComponent(pinRef)}`;
export const getGetGnssIotDacStateKey = (iotId: string, pinRef: string) =>
    ['gnss-iot-dac-state', iotId, pinRef] as const;

export async function getGnssIotDacState(
    iotId: string,
    pinRef: string,
    _request?: RequestInit,
): Promise<GnssIotDacStateData> {
    void _request;

    return loadNullableGnssData(() => getGnssIotDacStateResponse(iotId, pinRef), 'fetch GNSS IoT DAC state');
}

export type GetGnssIotDacStateQueryResult = GnssIotDacStateData;
export type GetGnssIotDacStateQueryError = Error;

export function useGetGnssIotDacState(
    iotId: null | string | undefined,
    pinRef: null | string | undefined,
    options?: GnssQueryHookOptions<GetGnssIotDacStateQueryResult>,
) {
    const enabled = Boolean(iotId) && Boolean(pinRef) && options?.swr?.enabled !== false;
    const swrKey = enabled && pinRef ? (options?.swr?.swrKey ?? getGetGnssIotDacStateKey(iotId ?? '', pinRef)) : null;
    const query = useSwr<GetGnssIotDacStateQueryResult, GetGnssIotDacStateQueryError>(
        swrKey,
        () => getGnssIotDacState(iotId ?? '', pinRef ?? '', options?.request),
        getGnssQueryOptions(options?.swr),
    );

    return buildGnssSwrQueryResult(swrKey, query);
}

export const getGetGnssFirmwareUrl = () => '/ota/latest';
export const getGetGnssFirmwareKey = () => ['gnss-firmware'] as const;

export async function getGnssFirmware(_request?: RequestInit): Promise<GnssFirmwareData> {
    void _request;

    return loadNullableGnssData(() => fetchGnssFirmwareResponse(), 'fetch GNSS firmware');
}

export type GetGnssFirmwareQueryResult = GnssFirmwareData;
export type GetGnssFirmwareQueryError = Error;

export function useGetGnssFirmware(options?: GnssQueryHookOptions<GetGnssFirmwareQueryResult>) {
    const enabled = options?.swr?.enabled !== false;
    const swrKey = enabled ? (options?.swr?.swrKey ?? getGetGnssFirmwareKey()) : null;
    const query = useSwr<GetGnssFirmwareQueryResult, GetGnssFirmwareQueryError>(
        swrKey,
        () => getGnssFirmware(options?.request),
        getGnssQueryOptions(options?.swr),
    );

    return buildGnssSwrQueryResult(swrKey, query);
}

export const getGetGnssAntennaManifestUrl = () => '/compensation/antennas/manifest';
export const getGetGnssAntennaManifestKey = () => ['gnss-antenna-manifest'] as const;

export async function getGnssAntennaManifest(_request?: RequestInit): Promise<AntennaManifest | null> {
    void _request;

    return loadNullableGnssData(() => getGnssAntennaManifestResponse(), 'fetch GNSS antenna manifest');
}

export type GetGnssAntennaManifestQueryResult = AntennaManifest | null;
export type GetGnssAntennaManifestQueryError = Error;

export function useGetGnssAntennaManifest(options?: GnssQueryHookOptions<GetGnssAntennaManifestQueryResult>) {
    const enabled = options?.swr?.enabled !== false;
    const swrKey = enabled ? (options?.swr?.swrKey ?? getGetGnssAntennaManifestKey()) : null;
    const query = useSwr<GetGnssAntennaManifestQueryResult, GetGnssAntennaManifestQueryError>(
        swrKey,
        () => getGnssAntennaManifest(options?.request),
        getGnssQueryOptions(options?.swr),
    );

    return buildGnssSwrQueryResult(swrKey, query);
}

export const getGetGnssDeviceAntennaManifestUrl = (deviceSerial: string) =>
    `/compensation/${encodeURIComponent(deviceSerial)}/antennas`;
export const getGetGnssDeviceAntennaManifestKey = (deviceSerial: string) =>
    ['gnss-device-antenna-manifest', deviceSerial] as const;

export async function getGnssDeviceAntennaManifest(
    deviceSerial: string,
    _request?: RequestInit,
): Promise<AntennaManifest | null> {
    void _request;

    return loadNullableGnssData(
        () => getGnssDeviceAntennaManifestResponse(deviceSerial),
        'fetch GNSS device antenna manifest',
    );
}

export type GetGnssDeviceAntennaManifestQueryResult = AntennaManifest | null;
export type GetGnssDeviceAntennaManifestQueryError = Error;

export function useGetGnssDeviceAntennaManifest(
    deviceSerial: null | string | undefined,
    options?: GnssQueryHookOptions<GetGnssDeviceAntennaManifestQueryResult>,
) {
    const enabled = Boolean(deviceSerial) && options?.swr?.enabled !== false;
    const swrKey = enabled ? (options?.swr?.swrKey ?? getGetGnssDeviceAntennaManifestKey(deviceSerial ?? '')) : null;
    const query = useSwr<GetGnssDeviceAntennaManifestQueryResult, GetGnssDeviceAntennaManifestQueryError>(
        swrKey,
        () => getGnssDeviceAntennaManifest(deviceSerial ?? '', options?.request),
        getGnssQueryOptions(options?.swr),
    );

    return buildGnssSwrQueryResult(swrKey, query);
}

export const getGetGnssAntennaDataJsonUrl = (antennaId: string) =>
    `/compensation/antennas/data/${encodeURIComponent(antennaId)}.json`;
export const getGetGnssAntennaDataJsonKey = (antennaId: string) => ['gnss-antenna-data-json', antennaId] as const;

export async function getGnssAntennaDataJson(antennaId: string, _request?: RequestInit): Promise<AntennaData | null> {
    void _request;

    return loadNullableGnssData(() => getGnssAntennaDataJsonResponse(antennaId), 'fetch GNSS antenna data json');
}

export type GetGnssAntennaDataJsonQueryResult = AntennaData | null;
export type GetGnssAntennaDataJsonQueryError = Error;

export function useGetGnssAntennaDataJson(
    antennaId: null | string | undefined,
    options?: GnssQueryHookOptions<GetGnssAntennaDataJsonQueryResult>,
) {
    const enabled = Boolean(antennaId) && options?.swr?.enabled !== false;
    const swrKey = enabled ? (options?.swr?.swrKey ?? getGetGnssAntennaDataJsonKey(antennaId ?? '')) : null;
    const query = useSwr<GetGnssAntennaDataJsonQueryResult, GetGnssAntennaDataJsonQueryError>(
        swrKey,
        () => getGnssAntennaDataJson(antennaId ?? '', options?.request),
        getGnssQueryOptions(options?.swr),
    );

    return buildGnssSwrQueryResult(swrKey, query);
}

export function useGnssPatchDeviceConfig(
    options?: Readonly<
        UseSdrMutationHookOptions<Awaited<ReturnType<typeof gnssPatchDeviceConfig>>, Error, GnssDeviceConfigUpdateArg>
    >,
) {
    return useGnssMutationAction({
        action: 'patch GNSS device config',
        execute: async (arg: GnssDeviceConfigUpdateArg) => gnssPatchDeviceConfig(arg.deviceSerial, arg.update),
        onSuccess: async (_response, arg) => invalidateGnssConfigReadModels(arg.deviceSerial),
        options,
        swrKey: ['gnss-patch-device-config'] as const,
    });
}

export function useGnssSetVswrCompensation(
    options?: Readonly<
        UseSdrMutationHookOptions<
            Awaited<ReturnType<typeof runEnableVswrCompensation>>,
            Error,
            GnssCompensationToggleArg
        >
    >,
) {
    return useGnssMutationAction({
        action: 'set GNSS VSWR compensation',
        execute: (arg: GnssCompensationToggleArg) => {
            if (arg.enabled) {
                return runEnableVswrCompensation(arg.deviceSerial);
            }

            return runDisableVswrCompensation(arg.deviceSerial);
        },
        onSuccess: async (_response, arg) => invalidateGnssCompensationReadModels(arg.deviceSerial),
        options,
        swrKey: ['gnss-set-vswr-compensation'] as const,
    });
}

export function useGnssSetNoiseFigureCompensation(
    options?: Readonly<
        UseSdrMutationHookOptions<
            Awaited<ReturnType<typeof runEnableNoiseFigureCompensation>>,
            Error,
            GnssCompensationToggleArg
        >
    >,
) {
    return useGnssMutationAction({
        action: 'set GNSS noise figure compensation',
        execute: (arg: GnssCompensationToggleArg) => {
            if (arg.enabled) {
                return runEnableNoiseFigureCompensation(arg.deviceSerial);
            }

            return runDisableNoiseFigureCompensation(arg.deviceSerial);
        },
        onSuccess: async (_response, arg) => invalidateGnssCompensationReadModels(arg.deviceSerial),
        options,
        swrKey: ['gnss-set-noise-figure-compensation'] as const,
    });
}

export function useGnssSetTemperatureCompensation(
    options?: Readonly<
        UseSdrMutationHookOptions<
            Awaited<ReturnType<typeof runEnableTemperatureCompensation>>,
            Error,
            GnssCompensationToggleArg
        >
    >,
) {
    return useGnssMutationAction({
        action: 'set GNSS temperature compensation',
        execute: (arg: GnssCompensationToggleArg) => {
            if (arg.enabled) {
                return runEnableTemperatureCompensation(arg.deviceSerial);
            }

            return runDisableTemperatureCompensation(arg.deviceSerial);
        },
        onSuccess: async (_response, arg) => invalidateGnssCompensationReadModels(arg.deviceSerial),
        options,
        swrKey: ['gnss-set-temperature-compensation'] as const,
    });
}

export function useGnssSetThresholds(
    options?: Readonly<
        UseSdrMutationHookOptions<Awaited<ReturnType<typeof gnssSetThresholds>>, Error, GnssThresholdsArg>
    >,
) {
    return useGnssMutationAction({
        action: 'set GNSS thresholds',
        execute: async (arg: GnssThresholdsArg) => gnssSetThresholds(arg.deviceSerial, arg.params),
        onSuccess: async (_response, arg) => invalidateGnssConfigReadModels(arg.deviceSerial),
        options,
        swrKey: ['gnss-set-thresholds'] as const,
    });
}

export function useGnssSwitchDeviceMode(
    options?: Readonly<
        UseSdrMutationHookOptions<Awaited<ReturnType<typeof gnssSwitchDeviceMode>>, Error, GnssSwitchDeviceModeArg>
    >,
) {
    return useGnssMutationAction({
        action: 'switch GNSS device mode',
        execute: async (arg: GnssSwitchDeviceModeArg) => gnssSwitchDeviceMode(arg.deviceSerial, arg.mode),
        onSuccess: async (_response, arg) => invalidateGnssDeviceModeState(arg.deviceSerial),
        options,
        swrKey: ['gnss-switch-device-mode'] as const,
    });
}

export function useGnssStartBaseline(
    options?: Readonly<
        UseSdrMutationHookOptions<Awaited<ReturnType<typeof gnssBaselineStart>>, Error, GnssDeviceSerialSweepsArg>
    >,
) {
    return useGnssMutationAction({
        action: 'start GNSS baseline',
        execute: async (arg: GnssDeviceSerialSweepsArg) => gnssBaselineStart(arg.deviceSerial, arg.sweeps),
        onSuccess: async (_response, arg) => invalidateGnssBaselineReadModels(arg.deviceSerial),
        options,
        swrKey: ['gnss-baseline-start'] as const,
    });
}

export function useGnssLoadBaselineFile(
    options?: Readonly<
        UseSdrMutationHookOptions<Awaited<ReturnType<typeof gnssLoadBaselineFile>>, Error, GnssDeviceSerialFilenameArg>
    >,
) {
    return useGnssMutationAction({
        action: 'load GNSS baseline file',
        execute: (arg: GnssDeviceSerialFilenameArg) => gnssLoadBaselineFile(arg.deviceSerial, arg.filename),
        onSuccess: async (_response, arg) => invalidateGnssBaselineReadModels(arg.deviceSerial),
        options,
        swrKey: ['gnss-baseline-load-file'] as const,
    });
}

export function useGnssStopBaseline(
    options?: Readonly<
        UseSdrMutationHookOptions<Awaited<ReturnType<typeof gnssBaselineStop>>, Error, GnssDeviceSerialArg>
    >,
) {
    return useGnssMutationAction({
        action: 'stop GNSS baseline',
        execute: async (arg: GnssDeviceSerialArg) => gnssBaselineStop(arg.deviceSerial),
        onSuccess: async (_response, arg) => invalidateGnssBaselineReadModels(arg.deviceSerial),
        options,
        swrKey: ['gnss-baseline-stop'] as const,
    });
}

export function useGnssStartBaselineBulk(
    options?: Readonly<
        UseSdrMutationHookOptions<Awaited<ReturnType<typeof gnssStartBaselineBulk>>, Error, GnssDeviceSerialsSweepsArg>
    >,
) {
    return useGnssMutationAction({
        action: 'start GNSS baseline in bulk',
        execute: async (arg: GnssDeviceSerialsSweepsArg) =>
            gnssStartBaselineBulk(Array.from(arg.deviceSerials), arg.sweeps),
        onSuccess: async (_response, arg) => {
            await Promise.all(arg.deviceSerials.map((deviceSerial) => invalidateGnssBaselineReadModels(deviceSerial)));
        },
        options,
        swrKey: ['gnss-baseline-start-bulk'] as const,
    });
}

export function useGnssStopBaselineBulk(
    options?: Readonly<
        UseSdrMutationHookOptions<Awaited<ReturnType<typeof gnssStopBaselineBulk>>, Error, GnssDeviceSerialsArg>
    >,
) {
    return useGnssMutationAction({
        action: 'stop GNSS baseline in bulk',
        execute: async (arg: GnssDeviceSerialsArg) => gnssStopBaselineBulk(Array.from(arg.deviceSerials)),
        onSuccess: async (_response, arg) => {
            await Promise.all(arg.deviceSerials.map((deviceSerial) => invalidateGnssBaselineReadModels(deviceSerial)));
        },
        options,
        swrKey: ['gnss-baseline-stop-bulk'] as const,
    });
}

export function useGnssStartLive(
    options?: Readonly<
        UseSdrMutationHookOptions<Awaited<ReturnType<typeof gnssLiveStart>>, Error, GnssDeviceSerialSessionArg>
    >,
) {
    return useGnssMutationAction({
        action: 'start GNSS live stream',
        execute: async (arg: GnssDeviceSerialSessionArg) => gnssLiveStart(arg.deviceSerial, arg.session),
        options,
        swrKey: ['gnss-live-start'] as const,
    });
}

export function useGnssStopLive(
    options?: Readonly<
        UseSdrMutationHookOptions<Awaited<ReturnType<typeof gnssLiveStop>>, Error, GnssDeviceSerialSessionArg>
    >,
) {
    return useGnssMutationAction({
        action: 'stop GNSS live stream',
        execute: async (arg: GnssDeviceSerialSessionArg) => gnssLiveStop(arg.deviceSerial, arg.session),
        options,
        swrKey: ['gnss-live-stop'] as const,
    });
}

export function useGnssStartLiveSubscription(
    options?: Readonly<
        UseSdrMutationHookOptions<Awaited<ReturnType<typeof gnssSubscribeStart>>, Error, GnssDeviceSerialSessionArg>
    >,
) {
    return useGnssMutationAction({
        action: 'subscribe to GNSS live stream',
        execute: async (arg: GnssDeviceSerialSessionArg) => gnssSubscribeStart(arg.deviceSerial, arg.session),
        options,
        swrKey: ['gnss-subscribe-start'] as const,
    });
}

export function useGnssStopLiveSubscription(
    options?: Readonly<
        UseSdrMutationHookOptions<Awaited<ReturnType<typeof gnssSubscribeStop>>, Error, GnssDeviceSerialSessionArg>
    >,
) {
    return useGnssMutationAction({
        action: 'unsubscribe from GNSS live stream',
        execute: async (arg: GnssDeviceSerialSessionArg) => gnssSubscribeStop(arg.deviceSerial, arg.session),
        options,
        swrKey: ['gnss-subscribe-stop'] as const,
    });
}

export function useGnssStartSweep(
    options?: Readonly<
        UseSdrMutationHookOptions<Awaited<ReturnType<typeof gnssStartSweep>>, Error, GnssDeviceSerialArg>
    >,
) {
    return useGnssMutationAction({
        action: 'start GNSS sweep',
        execute: async (arg: GnssDeviceSerialArg) => gnssStartSweep(arg.deviceSerial),
        options,
        swrKey: ['gnss-sweep-start'] as const,
    });
}

export function useGnssStopSweep(
    options?: Readonly<
        UseSdrMutationHookOptions<Awaited<ReturnType<typeof gnssStopSweep>>, Error, GnssDeviceSerialArg>
    >,
) {
    return useGnssMutationAction({
        action: 'stop GNSS sweep',
        execute: async (arg: GnssDeviceSerialArg) => gnssStopSweep(arg.deviceSerial),
        options,
        swrKey: ['gnss-sweep-stop'] as const,
    });
}

export function useGnssStartSweepBulk(
    options?: Readonly<UseSdrMutationHookOptions<Awaited<ReturnType<typeof gnssStartSweepBulk>>, Error, void>>,
) {
    return useGnssMutationAction({
        action: 'start GNSS sweep in bulk',
        execute: async () => gnssStartSweepBulk(),
        onSuccess: async () => invalidateGnssGlobalPrefixes([getGetGnssDevicesKey()[0]]),
        options,
        swrKey: ['gnss-sweep-start-bulk'] as const,
    });
}

export function useGnssStopSweepBulk(
    options?: Readonly<UseSdrMutationHookOptions<Awaited<ReturnType<typeof gnssStopSweepBulk>>, Error, void>>,
) {
    return useGnssMutationAction({
        action: 'stop GNSS sweep in bulk',
        execute: async () => gnssStopSweepBulk(),
        onSuccess: async () => invalidateGnssGlobalPrefixes([getGetGnssDevicesKey()[0]]),
        options,
        swrKey: ['gnss-sweep-stop-bulk'] as const,
    });
}

export function useGnssClearSweepArchives(
    options?: Readonly<
        UseSdrMutationHookOptions<Awaited<ReturnType<typeof gnssClearSweepArchives>>, Error, GnssDeviceSerialArg>
    >,
) {
    return useGnssMutationAction({
        action: 'clear GNSS sweep archives',
        execute: async (arg: GnssDeviceSerialArg) => gnssClearSweepArchives(arg.deviceSerial),
        onSuccess: async (_response, arg) => invalidateGnssDeviceArchives(arg.deviceSerial),
        options,
        swrKey: ['gnss-archive-clear-all'] as const,
    });
}

export function useGnssClearSweepArchiveFile(
    options?: Readonly<
        UseSdrMutationHookOptions<
            Awaited<ReturnType<typeof gnssClearSweepArchiveFile>>,
            Error,
            GnssDeviceSerialFilenameArg
        >
    >,
) {
    return useGnssMutationAction({
        action: 'clear a GNSS sweep archive file',
        execute: async (arg: GnssDeviceSerialFilenameArg) => gnssClearSweepArchiveFile(arg.deviceSerial, arg.filename),
        onSuccess: async (_response, arg) => invalidateGnssDeviceArchives(arg.deviceSerial),
        options,
        swrKey: ['gnss-archive-clear-file'] as const,
    });
}

export function useGnssDownloadAllSweepArchives(
    options?: Readonly<
        UseSdrMutationHookOptions<Awaited<ReturnType<typeof gnssDownloadAllSweepArchives>>, Error, GnssDeviceSerialArg>
    >,
) {
    return useGnssMutationAction({
        action: 'download all GNSS sweep archives',
        execute: async (arg: GnssDeviceSerialArg) => gnssDownloadAllSweepArchives(arg.deviceSerial),
        options,
        swrKey: ['gnss-archive-download-all'] as const,
    });
}

export function useGnssDownloadSweepArchiveFile(
    options?: Readonly<
        UseSdrMutationHookOptions<
            Awaited<ReturnType<typeof gnssDownloadSweepArchiveFile>>,
            Error,
            GnssDeviceSerialFilenameArg
        >
    >,
) {
    return useGnssMutationAction({
        action: 'download a GNSS sweep archive file',
        execute: async (arg: GnssDeviceSerialFilenameArg) =>
            gnssDownloadSweepArchiveFile(arg.deviceSerial, arg.filename),
        options,
        swrKey: ['gnss-archive-download-file'] as const,
    });
}

export function useGnssDownloadMeasurementReportFile(
    options?: Readonly<
        UseSdrMutationHookOptions<Awaited<ReturnType<typeof gnssDownloadMeasurementReportFile>>, Error, string>
    >,
) {
    return useGnssMutationAction({
        action: 'download a GNSS measurement report file',
        execute: async (filename: string) => gnssDownloadMeasurementReportFile(filename),
        options,
        swrKey: ['gnss-measurement-report-download-file'] as const,
    });
}

export function useGnssDownloadMeasurementReportBundle(
    options?: Readonly<
        UseSdrMutationHookOptions<Awaited<ReturnType<typeof gnssDownloadMeasurementReportBundle>>, Error, string>
    >,
) {
    return useGnssMutationAction({
        action: 'download a GNSS measurement report bundle',
        execute: async (date: string) => gnssDownloadMeasurementReportBundle(date),
        options,
        swrKey: ['gnss-measurement-report-download-bundle'] as const,
    });
}

export function useGnssDownloadMeasurementReportSummary(
    options?: Readonly<
        UseSdrMutationHookOptions<Awaited<ReturnType<typeof gnssDownloadMeasurementReportSummary>>, Error, void>
    >,
) {
    return useGnssMutationAction({
        action: 'download the GNSS measurement report summary',
        execute: async () => gnssDownloadMeasurementReportSummary(),
        options,
        swrKey: ['gnss-measurement-report-download-summary'] as const,
    });
}

export function useGnssStartSave(
    options?: Readonly<
        UseSdrMutationHookOptions<Awaited<ReturnType<typeof gnssStartSave>>, Error, GnssDeviceSerialArg>
    >,
) {
    return useGnssMutationAction({
        action: 'start GNSS saving',
        execute: async (arg: GnssDeviceSerialArg) => gnssStartSave(arg.deviceSerial),
        onSuccess: async (_response, arg) => invalidateGnssStatusReadModels(arg.deviceSerial),
        options,
        swrKey: ['gnss-save-start'] as const,
    });
}

export function useGnssStartSaveBulk(
    options?: Readonly<UseSdrMutationHookOptions<Awaited<ReturnType<typeof gnssStartSaveBulk>>, Error, void>>,
) {
    return useGnssMutationAction({
        action: 'start GNSS saving in bulk',
        execute: async () => gnssStartSaveBulk(),
        onSuccess: async () => invalidateGnssGlobalPrefixes([getGetGnssDevicesKey()[0]]),
        options,
        swrKey: ['gnss-save-start-bulk'] as const,
    });
}

export function useGnssStopSave(
    options?: Readonly<UseSdrMutationHookOptions<Awaited<ReturnType<typeof gnssStopSave>>, Error, GnssDeviceSerialArg>>,
) {
    return useGnssMutationAction({
        action: 'stop GNSS saving',
        execute: async (arg: GnssDeviceSerialArg) => gnssStopSave(arg.deviceSerial),
        onSuccess: async (_response, arg) => invalidateGnssStatusReadModels(arg.deviceSerial),
        options,
        swrKey: ['gnss-save-stop'] as const,
    });
}

export function useGnssStopSaveBulk(
    options?: Readonly<UseSdrMutationHookOptions<Awaited<ReturnType<typeof gnssStopSaveBulk>>, Error, void>>,
) {
    return useGnssMutationAction({
        action: 'stop GNSS saving in bulk',
        execute: async () => gnssStopSaveBulk(),
        onSuccess: async () => invalidateGnssGlobalPrefixes([getGetGnssDevicesKey()[0]]),
        options,
        swrKey: ['gnss-save-stop-bulk'] as const,
    });
}

export function useGnssRebootAll(
    options?: Readonly<UseSdrMutationHookOptions<Awaited<ReturnType<typeof gnssRebootAll>>, Error, void>>,
) {
    return useGnssMutationAction({
        action: 'reboot all GNSS devices',
        execute: async () => gnssRebootAll(),
        onSuccess: async () => invalidateGnssGlobalPrefixes([getGetGnssDevicesKey()[0]]),
        options,
        swrKey: ['gnss-reboot-all'] as const,
    });
}

export function useGnssRebootDevice(
    options?: Readonly<
        UseSdrMutationHookOptions<Awaited<ReturnType<typeof gnssRebootDevice>>, Error, GnssDeviceSerialArg>
    >,
) {
    return useGnssMutationAction({
        action: 'reboot a GNSS device',
        execute: async (arg: GnssDeviceSerialArg) => gnssRebootDevice(arg.deviceSerial),
        onSuccess: async (_response, arg) => invalidateGnssPowerReadModels(arg.deviceSerial),
        options,
        swrKey: ['gnss-reboot-device'] as const,
    });
}

export function useGnssRestartAll(
    options?: Readonly<UseSdrMutationHookOptions<Awaited<ReturnType<typeof gnssRestartAll>>, Error, void>>,
) {
    return useGnssMutationAction({
        action: 'restart all GNSS devices',
        execute: async () => gnssRestartAll(),
        onSuccess: async () => invalidateGnssGlobalPrefixes([getGetGnssDevicesKey()[0]]),
        options,
        swrKey: ['gnss-restart-all'] as const,
    });
}

export interface MeasurementReportUploadArg {
    readonly file: File;
    readonly replaceIfExists?: boolean;
}

export function useGnssUploadMeasurementReport(
    options?: Readonly<
        UseSdrMutationHookOptions<
            Awaited<ReturnType<typeof gnssUploadMeasurementReport>>,
            Error,
            MeasurementReportUploadArg
        >
    >,
) {
    return useGnssMutationAction({
        action: 'upload a GNSS measurement report',
        execute: async (arg: MeasurementReportUploadArg) => gnssUploadMeasurementReport(arg.file, arg.replaceIfExists),
        onSuccess: async () => invalidateGnssGlobalPrefixes([getGetGnssMeasurementReportsListKey()[0]]),
        options,
        swrKey: ['gnss-measurement-report-upload'] as const,
    });
}

export function useGnssUploadMeasurementReportBundle(
    options?: Readonly<
        UseSdrMutationHookOptions<
            Awaited<ReturnType<typeof gnssUploadMeasurementReportBundle>>,
            Error,
            MeasurementReportUploadArg
        >
    >,
) {
    return useGnssMutationAction({
        action: 'upload a GNSS measurement report bundle',
        execute: async (arg: MeasurementReportUploadArg) =>
            gnssUploadMeasurementReportBundle(arg.file, arg.replaceIfExists),
        onSuccess: async () => invalidateGnssGlobalPrefixes([getGetGnssMeasurementReportsListKey()[0]]),
        options,
        swrKey: ['gnss-measurement-report-upload-bundle'] as const,
    });
}

export function useGnssRestartDevice(
    options?: Readonly<
        UseSdrMutationHookOptions<Awaited<ReturnType<typeof gnssRestartDevice>>, Error, GnssDeviceSerialArg>
    >,
) {
    return useGnssMutationAction({
        action: 'restart a GNSS device',
        execute: async (arg: GnssDeviceSerialArg) => gnssRestartDevice(arg.deviceSerial),
        onSuccess: async (_response, arg) => invalidateGnssPowerReadModels(arg.deviceSerial),
        options,
        swrKey: ['gnss-restart-device'] as const,
    });
}

export function useGnssSaveLayout(
    options?: Readonly<UseSdrMutationHookOptions<Awaited<ReturnType<typeof gnssSaveLayout>>, Error, GnssLayoutSaveArg>>,
) {
    return useGnssMutationAction({
        action: 'save GNSS layout',
        execute: async (arg: GnssLayoutSaveArg) => gnssSaveLayout(arg.reference, arg.coords),
        onSuccess: async () => invalidateGnssLayoutData(),
        options,
        swrKey: ['gnss-layout-save'] as const,
    });
}

export function useGnssPushFirmware(
    options?: Readonly<
        UseSdrMutationHookOptions<Awaited<ReturnType<typeof gnssPushFirmware>>, Error, GnssPushFirmwareArg>
    >,
) {
    return useGnssMutationAction({
        action: 'push GNSS firmware',
        execute: async (arg: GnssPushFirmwareArg) => gnssPushFirmware(arg.scope, arg.version),
        onSuccess: async () => invalidateGnssFirmwareData(),
        options,
        swrKey: ['gnss-firmware-push'] as const,
    });
}

export function useGnssDownloadAntennaDataBin(
    options?: Readonly<
        UseSdrMutationHookOptions<Awaited<ReturnType<typeof gnssGetAntennaDataBin>>, Error, GnssDownloadAntennaBinArg>
    >,
) {
    return useGnssMutationAction({
        action: 'download GNSS antenna data bin',
        execute: async (arg: GnssDownloadAntennaBinArg) => gnssGetAntennaDataBin(arg.antennaId),
        options,
        swrKey: ['gnss-antenna-download-bin'] as const,
    });
}

export function useGnssSetIotDacValue(
    options?: Readonly<
        UseSdrMutationHookOptions<Awaited<ReturnType<typeof gnssSetIotDacValue>>, Error, GnssIotSetDacArg>
    >,
) {
    return useGnssMutationAction({
        action: 'set GNSS IoT DAC value',
        execute: async (arg: GnssIotSetDacArg) => gnssSetIotDacValue(arg.iotId, arg.pinRef, arg.value),
        onSuccess: async (_response, arg) => invalidateGnssIotRuntime(arg.iotId, arg.pinRef),
        options,
        swrKey: ['gnss-iot-dac-set'] as const,
    });
}

export function useGnssZeroIotDac(
    options?: Readonly<UseSdrMutationHookOptions<Awaited<ReturnType<typeof gnssZeroIotDac>>, Error, GnssIotIdArg>>,
) {
    return useGnssMutationAction({
        action: 'zero GNSS IoT DAC outputs',
        execute: async (arg: GnssIotIdArg) => gnssZeroIotDac(arg.iotId),
        onSuccess: async (_response, arg) => invalidateGnssIotRuntime(arg.iotId),
        options,
        swrKey: ['gnss-iot-dac-zero'] as const,
    });
}

export function useGnssTurnIotRelayOn(
    options?: Readonly<
        UseSdrMutationHookOptions<Awaited<ReturnType<typeof gnssTurnIotRelayOn>>, Error, GnssIotRelayOnArg>
    >,
) {
    return useGnssMutationAction({
        action: 'turn on GNSS IoT relay',
        execute: async (arg: GnssIotRelayOnArg) =>
            gnssTurnIotRelayOn(arg.iotId, arg.pinRef, arg.timeoutSeconds, arg.username),
        onSuccess: async (_response, arg) => invalidateGnssIotRuntime(arg.iotId),
        options,
        swrKey: ['gnss-iot-relay-on'] as const,
    });
}

export function useGnssTurnIotRelaysOn(
    options?: Readonly<
        UseSdrMutationHookOptions<Awaited<ReturnType<typeof gnssTurnIotRelaysOn>>, Error, GnssIotRelaysOnArg>
    >,
) {
    return useGnssMutationAction({
        action: 'turn on selected GNSS IoT relays',
        execute: async (arg: GnssIotRelaysOnArg) =>
            gnssTurnIotRelaysOn(arg.iotId, arg.username, {
                pin_refs: Array.from(arg.pinRefs),
                timeout_s: arg.timeoutSeconds ?? null,
                value: arg.value,
            }),
        onSuccess: async (_response, arg) => invalidateGnssIotRuntime(arg.iotId),
        options,
        swrKey: ['gnss-iot-relays-on'] as const,
    });
}

export function useGnssTurnIotRelayOff(
    options?: Readonly<
        UseSdrMutationHookOptions<Awaited<ReturnType<typeof gnssTurnIotRelayOff>>, Error, GnssIotRelayOffArg>
    >,
) {
    return useGnssMutationAction({
        action: 'turn off GNSS IoT relay',
        execute: async (arg: GnssIotRelayOffArg) => gnssTurnIotRelayOff(arg.iotId, arg.pinRef, arg.username),
        onSuccess: async (_response, arg) => invalidateGnssIotRuntime(arg.iotId),
        options,
        swrKey: ['gnss-iot-relay-off'] as const,
    });
}

export function useGnssSaveIotBinding(
    options?: Readonly<
        UseSdrMutationHookOptions<Awaited<ReturnType<typeof gnssSaveIotBinding>>, Error, GnssIotBindingArg>
    >,
) {
    return useGnssMutationAction({
        action: 'save GNSS IoT binding',
        execute: async (arg: GnssIotBindingArg) =>
            gnssSaveIotBinding(arg.iotId, arg.pinId, {
                alt: arg.alt ?? 0,
                bound_device_id: arg.boundDeviceId,
                kind: arg.kind ?? null,
                label: arg.label ?? null,
                lat: arg.lat,
                lon: arg.lon,
            }),
        onSuccess: async (_response, arg) => invalidateGnssIotBindingsAndEvents(arg.iotId),
        options,
        swrKey: ['gnss-iot-binding-save'] as const,
    });
}

export function useGnssRemoveIotBinding(
    options?: Readonly<
        UseSdrMutationHookOptions<Awaited<ReturnType<typeof gnssRemoveIotBinding>>, Error, GnssIotBindingDeleteArg>
    >,
) {
    return useGnssMutationAction({
        action: 'remove GNSS IoT binding',
        execute: async (arg: GnssIotBindingDeleteArg) => gnssRemoveIotBinding(arg.iotId, arg.pinId),
        onSuccess: async (_response, arg) => invalidateGnssIotBindingsAndEvents(arg.iotId),
        options,
        swrKey: ['gnss-iot-binding-remove'] as const,
    });
}

export {
    useGnssClearSweepArchiveFile as useClearGnssSweepArchiveFile,
    useGnssClearSweepArchives as useClearGnssSweepArchives,
    useGnssDownloadAllSweepArchives as useDownloadAllGnssSweepArchives,
    useGnssDownloadAntennaDataBin as useDownloadGnssAntennaDataBin,
    useGnssDownloadMeasurementReportBundle as useDownloadGnssMeasurementReportBundle,
    useGnssDownloadMeasurementReportFile as useDownloadGnssMeasurementReportFile,
    useGnssDownloadMeasurementReportSummary as useDownloadGnssMeasurementReportSummary,
    useGnssDownloadSweepArchiveFile as useDownloadGnssSweepArchiveFile,
    useGnssLoadBaselineFile as useLoadGnssBaselineFile,
    useGnssPatchDeviceConfig as usePatchGnssDeviceConfig,
    useGnssPushFirmware as usePushGnssFirmware,
    useGnssRebootAll as useRebootGnssAll,
    useGnssRebootDevice as useRebootGnssDevice,
    useGnssRemoveIotBinding as useRemoveGnssIotBinding,
    useGnssRestartAll as useRestartGnssAll,
    useGnssRestartDevice as useRestartGnssDevice,
    useGnssSaveIotBinding as useSaveGnssIotBinding,
    useGnssSaveLayout as useSaveGnssLayout,
    useGnssSetIotDacValue as useSetGnssIotDacValue,
    useGnssSetNoiseFigureCompensation as useSetGnssNoiseFigureCompensation,
    useGnssSetTemperatureCompensation as useSetGnssTemperatureCompensation,
    useGnssSetThresholds as useSetGnssThresholds,
    useGnssSetVswrCompensation as useSetGnssVswrCompensation,
    useGnssStartBaseline as useStartGnssBaseline,
    useGnssStartBaselineBulk as useStartGnssBaselineBulk,
    useGnssStartLive as useStartGnssLive,
    useGnssStartLiveSubscription as useStartGnssLiveSubscription,
    useGnssStartSave as useStartGnssSave,
    useGnssStartSaveBulk as useStartGnssSaveBulk,
    useGnssStartSweep as useStartGnssSweep,
    useGnssStartSweepBulk as useStartGnssSweepBulk,
    useGnssStopBaseline as useStopGnssBaseline,
    useGnssStopBaselineBulk as useStopGnssBaselineBulk,
    useGnssStopLive as useStopGnssLive,
    useGnssStopLiveSubscription as useStopGnssLiveSubscription,
    useGnssStopSave as useStopGnssSave,
    useGnssStopSaveBulk as useStopGnssSaveBulk,
    useGnssStopSweep as useStopGnssSweep,
    useGnssStopSweepBulk as useStopGnssSweepBulk,
    useGnssSwitchDeviceMode as useSwitchGnssDeviceMode,
    useGnssTurnIotRelayOff as useTurnOffGnssIotRelay,
    useGnssTurnIotRelayOn as useTurnOnGnssIotRelay,
    useGnssTurnIotRelaysOn as useTurnOnGnssIotRelays,
    useGnssUploadMeasurementReport as useUploadGnssMeasurementReport,
    useGnssUploadMeasurementReportBundle as useUploadGnssMeasurementReportBundle,
    useGnssZeroIotDac as useZeroGnssIotDac,
};
